'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import UIApplication from 'UIComponents/app/UIApplication';
import UIBox from 'UIComponents/layout/UIBox';
import MarketingNavHeader from 'ecosystem-marketplace-core/components/nav/MarketingNavHeader';
import MarketingNavFooter from 'ecosystem-marketplace-core/components/nav/MarketingNavFooter';
import { useWindowDimensionsContext } from 'ecosystem-marketplace-core/hooks/useWindowDimensionsContext';
import useShortlinkParams from '../utils/hooks/useShortlinkParams';
import { getAppShortlink, getSignupUrl, getContactUrl, getLocalizedPublicPath, getLocalizedPublicUrl } from '../utils/urls';

const getI18nKey = key => `appMarketplace.publicApplication.${key}`;

const StyledUIApplication = styled(UIApplication).withConfig({
  displayName: "PublicApplication__StyledUIApplication",
  componentId: "sc-1w38af8-0"
})(["display:flex;flex-direction:column;"]);
const StyledMainContent = styled(props => /*#__PURE__*/_jsx(UIBox, Object.assign({}, props, {
  grow: 1,
  shrink: 1
}))).withConfig({
  displayName: "PublicApplication__StyledMainContent",
  componentId: "sc-1w38af8-1"
})(["min-height:100vh;"]);

const PublicApplication = (_ref) => {
  let {
    children
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["children"]);

  const {
    origin: hostName
  } = window.location;
  const {
    pathname
  } = useLocation();
  const shortlinkParams = useShortlinkParams();
  const lang = getLang();
  const languagesContent = useMemo(() => {
    return {
      en: ['English', hostName, getLocalizedPublicPath('en', pathname)],
      jp: ['日本語', hostName, getLocalizedPublicPath('ja', pathname)],
      de: ['Deutsch', hostName, getLocalizedPublicPath('de', pathname)],
      es: ['Español', hostName, getLocalizedPublicPath('es', pathname)],
      pt: ['Português', hostName, getLocalizedPublicPath('pt', pathname)],
      fr: ['Français', hostName, getLocalizedPublicPath('fr', pathname)],
      nl: ['Nederlands', hostName, getLocalizedPublicPath('nl', pathname)],
      it: ['Italiano', hostName, getLocalizedPublicPath('it', pathname)]
    };
  }, [hostName, pathname]);
  const headerContentBlob = useMemo(() => {
    const contactText = unescapedText(getI18nKey('header.contactText'));
    const contactUrl = getContactUrl(lang);
    const ctaText = unescapedText(getI18nKey('header.ctaText'));
    const ctaUrl = getSignupUrl(lang, pathname);
    return {
      upperNavContent: {
        homeUrl: getLocalizedPublicUrl(lang, ''),
        loginUrl: getAppShortlink(pathname, shortlinkParams),
        loginText: unescapedText(getI18nKey('header.loginText')),
        ctaUrl,
        ctaText,
        regionalDropdownContent: {
          contactUrl,
          contactText,
          languagesContent
        }
      },
      mobileNavContent: {
        contactUrl,
        contactText,
        ctaUrl,
        ctaText,
        languagesContent,
        searchPlaceholder: unescapedText(getI18nKey('header.searchPlaceholder'))
      }
    };
  }, [lang, languagesContent, pathname, shortlinkParams]);
  const {
    width: windowWidth
  } = useWindowDimensionsContext();
  const headerVariation = windowWidth < 768 ? 'default' : 'marketplace';
  return /*#__PURE__*/_jsxs(StyledUIApplication, Object.assign({}, rest, {
    children: [/*#__PURE__*/_jsx(UIBox, {
      grow: 0,
      shrink: 0,
      children: /*#__PURE__*/_jsx(MarketingNavHeader, {
        contentBlob: headerContentBlob,
        enableConvertFlag: true,
        lang: lang,
        variation: headerVariation
      })
    }), /*#__PURE__*/_jsx(StyledMainContent, {
      children: children
    }), /*#__PURE__*/_jsx(UIBox, {
      grow: 0,
      shrink: 0,
      children: /*#__PURE__*/_jsx(MarketingNavFooter, {
        lang: lang
      })
    })]
  }));
};

PublicApplication.propTypes = {
  children: PropTypes.node
};
export default PublicApplication;